import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import type { ProductSelectLayout } from "@lib/config/types";
import { type IndividualProduct } from "@lib/products";
import { resolveFontFamilyVariables, type FontFamily } from "@lib/utils";
import { Root as RadioGroup } from "@radix-ui/react-radio-group";
import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { StyleProvider } from "../form/style";
import { useTranslations } from "../../context/translations";
import { ProductSwatchRadio } from "../products/product-swatch-radio";
import { type ProductSelectProps } from "./product-select-v2";
import { getHasShade, useSelectedShade } from "./shade-selector";

type FormStyles = CSSProperties & {
  "--input-border-width": string;
  "--input-color": string;
  "--input-background": string;
  "--input-border-color": string;
  "--input-radius": string;
  "--input-font-family": string;
  "--input-font-weight": string;
  "--input-font-style": string;
  "--label-color": string;
  "--label-font-family": string;
  "--label-font-weight": string;
  "--label-font-style": string;
  "--placeholder-color": string;
  "--field-border-size": string;
};

function getSelectStyles(layout: ProductSelectLayout) {
  if (!getHasShade(layout)) return {};
  const shade = layout.shade;

  const isPill =
    shade.chosen_shade.field_style.border.radius === 9999.0 ||
    shade.chosen_shade.field_style.border.radius === 99999.0;

  return {
    "--input-border-width": `${shade.chosen_shade.field_style.border.width}px`,
    "--input-color": shade.chosen_shade.field_style.color,
    "--input-background": shade.chosen_shade.field_style.background_color,
    "--input-border-color": shade.chosen_shade.field_style.border.color,
    "--input-radius": `${isPill ? "9999em" : `min(${shade.chosen_shade.field_style.border.radius}px, 1.5625rem)`}`,
    ...resolveFontFamilyVariables(
      "--label",
      shade.chosen_shade.label_text.font as FontFamily,
      shade.chosen_shade.label_text.font_properties,
      shade.chosen_shade.label_text.font_weight,
    ),
    "--label-color": shade.chosen_shade.label_text.color,
    ...resolveFontFamilyVariables(
      "--input",
      shade.chosen_shade.field_style.font as FontFamily,
      shade.chosen_shade.field_style.font_properties,
      shade.chosen_shade.field_style.font_weight,
    ),
    "--placeholder-color": shade.chosen_shade.field_style.placeholder_color,
    "--field-border-size": `${shade.chosen_shade.field_style.border.width}px`,
  } as FormStyles;
}

/**
 * Grouped shade selector field with tabs
 */
export function ShadeSelectorFieldWithGroups(props: ProductSelectProps) {
  const { control, setValue } = useFormContext();
  const { product_selection: productSelect, activity } = props;

  if (
    activity.products.type === "single" ||
    activity.products.type === "bundle"
  )
    return null;

  const { layout } = productSelect;

  const products = activity.products.product.products;

  const defaultAllocationId = useMemo(() => {
    if (getHasShade(layout) && products) {
      return (
        products.find(({ hasStock }) => hasStock)?.allocationIds.join(",") ?? ""
      );
    }
    return "";
  }, [layout, products]);

  useEffect(() => {
    setValue("order_allocation_ids", defaultAllocationId);
  }, [products, setValue]);

  return (
    <>
      <FormField
        control={control}
        name="order_allocation_ids"
        render={({
          field,
        }: {
          field: {
            name: string;
            value: string;
            onChange: (value: string) => void;
          };
        }) => (
          <>
            <ShadeSelectorTabs {...props} field={field} />
            <ShadeSelectorDropdown {...props} field={field} />
          </>
        )}
      />
    </>
  );
}

function ShadeSelectorTabs(
  props: ProductSelectProps & {
    field: {
      name: string;
      value: string;
      onChange: (value: string) => void;
    };
  },
) {
  const { product_selection: productSelect, activity, field } = props;
  const t = useTranslations();
  const { selectedShade } = useSelectedShade(props);
  const [activeTab, setActiveTab] = useState("all");
  const containerRef = useRef<HTMLDivElement>(null);

  if (
    activity.products.type === "single" ||
    activity.products.type === "bundle"
  )
    return null;

  const { layout } = productSelect;

  const products = activity.products.product.products;

  const filteredProducts = products.filter(
    (
      product,
    ): product is {
      allocationIds: string[];
      hasStock: boolean;
      allocation: string;
      external_id: string | null;
      image: string;
      name: string;
      shade: string | null;
      category: string | null;
      display_index: number;
      productIds: string[];
    } => "allocationIds" in product && "hasStock" in product,
  );

  const categoryMap = filteredProducts.reduce<
    Record<string, typeof filteredProducts>
  >((acc, product) => {
    if (!product.category) return acc;
    const category = product.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {});

  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollLeft = el.scrollLeft;
    const clientWidth = el.clientWidth;
    const scrollWidth = el.scrollWidth;

    const threshold = 4;

    setAtStart(scrollLeft <= threshold);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - threshold);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const scrollByAmount = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <FormItem>
      <FormControl>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="relative">
            {!atStart && (
              <div
                role="button"
                aria-controls="tabs-list"
                aria-label={t("Scroll categories backward")}
                className={`absolute top-0 left-0 bottom-4 w-6 z-10  flex items-center justify-start bg-[--bg] transition-opacity before:absolute before:-right-6 before:w-6 before:bg-gradient-to-r before:from-[--bg] before:to-transparent before:inset-y-0 before:pointer-events-none cursor-pointer  ${
                  atStart ? "opacity-0" : "opacity-100"
                }`}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (scrollRef.current?.clientWidth) {
                      scrollByAmount(scrollRef.current.clientWidth * -1);
                    }
                  }
                }}
                onClick={() => {
                  if (scrollRef.current?.clientWidth) {
                    scrollByAmount(scrollRef.current.clientWidth * -1);
                  }
                }}
              >
                <ChevronLeft width={16} height={16} />
              </div>
            )}

            <div
              ref={scrollRef}
              className="overflow-x-auto whitespace-nowrap no-scrollbar relative scroll-smooth snap-x snap-mandatory"
            >
              <span id="tabs-list-description" className="sr-only">
                {t("Select a category to view products")}
              </span>
              <TabsList
                id="tabs-list"
                aria-describedby="tabs-list-description"
                className="flex min-w-max gap-3 pb-0 bg-transparent mb-4 text-inherit border-b rounded-none justify-center"
              >
                <TabsTrigger
                  key="all"
                  value="all"
                  className="text-inherit rounded-none data-[state=active]:shadow-none border-b-2 border-transparent aria-selected:border-current bg-transparent! aria-selected:opacity-100 bg-[--bg] data-[state=active]:bg-[--bg] aria-selected:bg-[--bg] aria-selected:shadow-none"
                >
                  {t("All")}
                </TabsTrigger>
                {Object.keys(categoryMap).map((category) => (
                  <TabsTrigger
                    key={category}
                    value={category}
                    className="text-inherit rounded-none data-[state=active]:shadow-none border-b-2 border-transparent aria-selected:border-current aria-selected:opacity-100 bg-[--bg] data-[state=active]:bg-[--bg] aria-selected:bg-[--bg] aria-selected:shadow-none"
                  >
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {!atEnd && (
              <div
                role="button"
                aria-controls="tabs-list"
                aria-label={t("Scroll categories forward")}
                className={`absolute top-0 right-0 bottom-4 w-6 z-10  flex items-center justify-end bg-[--bg] transition-opacity before:absolute before:-left-6 before:w-6 before:bg-gradient-to-r before:from-transparent before:to-[--bg] before:inset-y-0 before:pointer-events-none cursor-pointer ${
                  atEnd ? "opacity-0" : "opacity-100"
                }`}
                tabIndex={0}
                onClick={() =>
                  scrollByAmount(scrollRef.current?.clientWidth ?? 0)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    scrollByAmount(scrollRef.current?.clientWidth ?? 0);
                  }
                }}
              >
                <ChevronRight width={16} height={16} />
              </div>
            )}
          </div>

          <TabsContent key="all" value="all">
            <RadioGroup
              value={field.value}
              name={field.name}
              onValueChange={(value) => {
                field.onChange(value);
              }}
              style={{
                color: productSelect.color,
                fontSize: productSelect.font_size,
              }}
              className="grid gap-2 justify-center grid-cols-swatches [&>div]:aspect-square"
            >
              {products.map((product) => (
                <ProductSwatchRadio
                  key={`all-${product.name.replace(/\s+/g, "")}`}
                  product={product as IndividualProduct}
                  selectedShade={selectedShade?.name}
                  layout={layout}
                  swatchClassName="aspect-square"
                  container={containerRef.current}
                />
              ))}
            </RadioGroup>
          </TabsContent>
          {Object.entries(categoryMap).map(([category, products]) => (
            <TabsContent key={category} value={category}>
              <div className="grid gap-2 justify-center grid-cols-swatches [&>div]:aspect-square">
                {products.map((product) => (
                  <RadioGroup
                    value={field.value}
                    name={field.name}
                    onValueChange={(value) => {
                      field.onChange(value);
                    }}
                    style={{
                      color: productSelect.color,
                      fontSize: productSelect.font_size,
                    }}
                    className="grid gap-2 justify-center grid-cols-swatches [&>div]:aspect-square"
                  >
                    <ProductSwatchRadio
                      key={`categorised-${product.name.replace(/\s+/g, "")}`}
                      product={product}
                      selectedShade={selectedShade?.name}
                      layout={layout}
                      swatchClassName="aspect-square w-10 flex-shrink-0"
                      container={containerRef.current}
                    />
                  </RadioGroup>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </FormControl>
    </FormItem>
  );
}

function ShadeSelectorDropdown(
  props: ProductSelectProps & {
    field: {
      name: string;
      value: string;
      onChange: (value: string) => void;
    };
  },
) {
  const { control } = useFormContext();
  const { product_selection: productSelect, activity, field } = props;

  if (
    activity.products.type === "single" ||
    activity.products.type === "bundle"
  )
    return null;

  const { layout, out_of_stock_message } = productSelect;

  const products = activity.products.product.products;

  const { selectedShade, defaultAllocationId } = useSelectedShade(props);

  const selectedAllocationId = useWatch({
    control,
    name: "order_allocation_ids",
    defaultValue: defaultAllocationId,
  }) as string;

  function getProductShade(product: IndividualProduct) {
    return getHasShade(layout) && "shade" in product && product.shade
      ? product.shade
      : "#000000";
  }

  function selectSwatchStyle(product: IndividualProduct) {
    const color = getProductShade(product);
    if (!getHasShade(layout)) return "none";

    if (layout.shade.shade_shape === "round") {
      return `radial-gradient(circle, ${color} 60%, transparent 61%)`;
    } else {
      return `linear-gradient(${color}, ${color})`;
    }
  }
  const [activeTab, setActiveTab] = useState("all");

  function handleSelect(allocationId: string) {
    const product = products.find(
      ({ allocationIds }) => allocationIds.join(",") === allocationId,
    ) as IndividualProduct;
    if (product) {
      if (activeTab === "all") return;
      if (product.category) setActiveTab(product.category);
    }
  }

  return (
    <FormItem>
      <FormControl>
        <div className="pt-8" style={{ ...getSelectStyles(layout) }}>
          <Label
            htmlFor="select-shade"
            className="mb-[0.87em] block text-[var(--label-color)] font-[var(--label-font-name)] weight-[var(--label-font-weight)]"
          >
            {getHasShade(layout)
              ? layout.shade.chosen_shade.label_text.copy
              : ""}
          </Label>
          <StyleProvider style={getSelectStyles(layout)}>
            <Select
              name={field.name}
              onValueChange={(value) => {
                handleSelect(value);
                field.onChange(value);
              }}
              value={selectedAllocationId}
            >
              <FormControl>
                <SelectTrigger
                  style={{
                    borderWidth: "var(--input-border-width)",
                  }}
                  className="flex gap-3 items-center justify-start ps-2 [&_svg:last-child]:ms-auto py-0 border-[--input-border-color] bg-[--input-background] text-[--input-color] rounded-[--input-radius]"
                >
                  {selectedShade && (
                    <span
                      className={`h-7 aspect-square ${getHasShade(layout) ? (layout.shade.shade_shape === "round" ? "rounded-full" : "") : ""}`}
                      style={{
                        background: `${selectedShade?.shade}`,
                      }}
                      aria-hidden="true"
                    ></span>
                  )}

                  <span>
                    {selectedShade ? (
                      <>
                        {selectedShade.name}
                        {!selectedShade.hasStock && (
                          <span className="text-xs">
                            &nbsp;&nbsp; ({out_of_stock_message})
                          </span>
                        )}
                      </>
                    ) : // @TODO:
                    // This should never happen because the first
                    // shade is always pre-selected
                    null}
                  </span>
                </SelectTrigger>
              </FormControl>
              <SelectContent id="select-shade">
                {products.map((product) => (
                  <SelectItem
                    onClick={() => {}}
                    key={product.name}
                    disabled={!product.hasStock}
                    value={product.allocationIds.join(",")}
                    className="ps-10 pt-2 aria-disabled:!opacity-100 [&>span]:right-2 [&>span]:left-auto group"
                    style={{
                      backgroundImage: selectSwatchStyle(
                        product as IndividualProduct,
                      ),
                      backgroundSize: "28px 28px",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "4px 4px",
                    }}
                  >
                    <span className="group-aria-disabled:opacity-50">
                      {product.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </StyleProvider>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  );
}
